/**
 * Flow Optimizer formula — Evaluator
 * For Media Devoted | Created By Ciro Autiero
 *
 * Evaluates a parsed formula AST against an item's metrics. NaN/Infinity
 * are coerced to 0 so a single bad metric value never poisons the score.
 */

import { parseFormula } from "./ast";
import type { AllowedFn, AstNode } from "./ast";
import { lowestValue } from "./lowest/lowest";

export type Metrics = Record<string, number>;

const FN_TABLE: Record<AllowedFn, (args: number[]) => number> = {
  min: (args) => Math.min(...(args.length ? args : [0])),
  max: (args) => Math.max(...(args.length ? args : [0])),
  abs: (args) => Math.abs(args[0] ?? 0),
  log: (args) => Math.log10(toPositive(args[0] ?? 0)),
  ln: (args) => Math.log(toPositive(args[0] ?? 0)),
  sqrt: (args) => Math.sqrt(Math.max(0, args[0] ?? 0)),
  // Only reached without a metric argument the parser refuses anyway.
  lowest: (args) => -(args[0] ?? 0),
};

/**
 * Parse + evaluate. Throws on malformed expressions.
 */
export function evaluateFormula(source: string, metrics: Metrics): number {
  if (!source.trim()) return 0;
  const ast = parseFormula(source);
  return safe(walk(ast, metrics));
}

/**
 * Validate-only: parse without evaluating. Throws on malformed expressions.
 */
export function validateFormula(source: string): void {
  if (!source.trim()) return;
  parseFormula(source);
}

/**
 * Distinct identifiers referenced by an expression (metric ids + `__sub_*`
 * refs). Throws on malformed expressions — validate first or wrap.
 */
export function collectFormulaIdentifiers(source: string): string[] {
  if (!source.trim()) return [];
  const names = new Set<string>();
  collectIdents(parseFormula(source), names);
  return [...names];
}

function collectIdents(node: AstNode, out: Set<string>): void {
  if (node.kind === "ident") out.add(node.name);
  else if (node.kind === "unary") collectIdents(node.operand, out);
  else if (node.kind === "binary") {
    collectIdents(node.left, out);
    collectIdents(node.right, out);
  } else if (node.kind === "call") {
    for (const arg of node.args) collectIdents(arg, out);
  }
}

function walk(node: AstNode, metrics: Metrics): number {
  if (node.kind === "num") return node.value;
  if (node.kind === "ident") return readMetric(metrics, node.name);
  if (node.kind === "unary") return -walk(node.operand, metrics);
  if (node.kind === "call") return walkCall(node, metrics);
  return walkBinary(node, metrics);
}

function walkBinary(
  node: Extract<AstNode, { kind: "binary" }>,
  metrics: Metrics,
): number {
  const left = walk(node.left, metrics);
  const right = walk(node.right, metrics);
  switch (node.op) {
    case "+":
      return left + right;
    case "-":
      return left - right;
    case "*":
      return left * right;
    case "/":
      return right === 0 ? 0 : left / right;
    case "%":
      return right === 0 ? 0 : left % right;
  }
}

function walkCall(
  node: Extract<AstNode, { kind: "call" }>,
  metrics: Metrics,
): number {
  const arg = node.args[0];
  if (node.name === "lowest" && arg?.kind === "ident")
    return lowestValue(metrics, arg.name);
  const args = node.args.map((a) => walk(a, metrics));
  return FN_TABLE[node.name](args);
}

function readMetric(metrics: Metrics, name: string): number {
  const raw = metrics[name];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
}

function toPositive(value: number): number {
  return value <= 0 ? 1e-9 : value;
}

function safe(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
